import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type FeedbackEntry } from "../lib/api";

export const VERDICT_LABELS: [string, string][] = [
  ["wrong", "got it wrong"],
  ["confusing", "confusing"],
  ["didnt_finish", "didn't finish"],
];

/**
 * The verdict, editable IN PLACE on a published entry.
 *
 * Not a convenience for second thoughts. THE SIGNAL WE MOST NEED ARRIVES AFTER THE ENTRY DOES:
 * a message that went to the wrong person is discovered when the recipient replies, hours later.
 * By then the entry is published and probably marked "confusing", because at writing time that was
 * all they knew. The upgrade from "confusing" to "wrong", made later, IS the late-discovery class —
 * the one thing no instrument of ours can see. If that correction costs reopening a form it will
 * not happen, and the row we most need sits there mislabelled.
 */
export function VerdictChips({ entry, readOnly }: { entry: FeedbackEntry; readOnly?: boolean }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (next: string[]) => api.setVerdicts(entry.id, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
  const current = entry.verdicts ?? [];

  // The gap between publishing and marking is itself the finding, so it is shown rather than
  // merely stored — otherwise nobody reading the page ever sees the late ones as late.
  const lateBy = entry.verdict_ts && entry.verdict_ts - entry.created_ts > 5 * 60_000
    ? Math.round((entry.verdict_ts - entry.created_ts) / 60_000)
    : null;

  return (
    <span>
      {VERDICT_LABELS.map(([value, label]) => {
        const on = current.includes(value);
        if (readOnly && !on) return null;
        return (
          <button
            key={value}
            type="button"
            className={on ? "chip on" : "chip"}
            aria-pressed={on}
            disabled={readOnly || save.isPending}
            title={readOnly ? label : on ? `remove "${label}"` : `mark "${label}"`}
            onClick={() => save.mutate(on ? current.filter((v) => v !== value) : [...current, value])}
            style={{ marginRight: 4 }}
          >
            {label}
          </button>
        );
      })}
      {lateBy !== null && (
        <span className="muted" style={{ fontSize: 11, marginRight: 4 }} title="Marked after the entry was published — the failure was not visible at writing time.">
          marked {lateBy < 90 ? `${lateBy}m` : `${Math.round(lateBy / 60)}h`} later
        </span>
      )}
      {save.isError && <span className="error-bar">{String(save.error)}</span>}
    </span>
  );
}
